import axios from 'axios';
import { customFetch } from '../lib/network/Axios';
import { handleError } from '../lib/ErrorHandler';
import type {
  CreateManualFundPayload,
  FundQueryParams,
  FundRecord,
  FundsListResponse,
  UpdateFundPayload,
} from '../types/fund.types';

function extractMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }
    if (Array.isArray(responseData?.message) && responseData.message.length > 0) {
      const first = responseData.message[0];
      if (typeof first === 'string' && first.trim()) {
        return first;
      }
    }
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

export const getFunds = async (params?: FundQueryParams): Promise<FundsListResponse> => {
  try {
    const response = await customFetch.get('/funds', {
      params: {
        page: params?.page || 1,
        limit: params?.limit || 20,
        search: params?.search || undefined,
        fundingType: params?.fundingType || undefined,
        status: params?.status || undefined,
        programCode: params?.programCode || undefined,
        startDate: params?.startDate || undefined,
        endDate: params?.endDate || undefined,
      },
    });

    return response.data as FundsListResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to fetch funds list'));
  }
};

export const getFundById = async (id: number): Promise<FundRecord> => {
  try {
    const response = await customFetch.get(`/funds/${id}`);
    return response.data as FundRecord;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to fetch fund details'));
  }
};

export const updateFund = async (id: number, payload: UpdateFundPayload): Promise<FundRecord> => {
  try {
    const response = await customFetch.patch(`/funds/${id}`, payload);
    return response.data as FundRecord;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to update fund'));
  }
};

export const createManualFund = async (payload: CreateManualFundPayload): Promise<FundRecord> => {
  try {
    const response = await customFetch.post('/funds/manual', payload);
    return response.data?.fund || response.data;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to create fund record'));
  }
};

export const deleteFund = async (id: number): Promise<{ success: boolean }> => {
  try {
    const response = await customFetch.delete(`/funds/${id}`);
    return response.data;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to delete fund'));
  }
};
